import { useEffect, useRef, useState } from "react";

const DEFAULT_PIXELS_PER_STEP = 4;
const DRAG_THRESHOLD = 3;

const useNumberScrub = ({
  disabled,
  getValue,
  onScrub,
  onScrubEnd,
  step = 1,
  pixelsPerStep = DEFAULT_PIXELS_PER_STEP,
}) => {
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [isScrubbing, setIsScrubbing] = useState(false);

  const resetDocumentCursor = () => {
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };

  const finishScrub = (event) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;

    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    dragRef.current = null;
    resetDocumentCursor();
    setIsScrubbing(false);

    if (drag.moved) {
      suppressClickRef.current = true;
      onScrubEnd?.();
    }
  };

  useEffect(
    () => () => {
      resetDocumentCursor();
    },
  );

  const onPointerDown = (event) => {
    if (disabled || event.button !== 0) return;

    const startValue = Number(getValue());
    if (!Number.isFinite(startValue)) return;

    dragRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startValue,
      lastStepCount: 0,
      moved: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const distance = drag.startY - event.clientY;
    if (!drag.moved && Math.abs(distance) < DRAG_THRESHOLD) return;

    if (!drag.moved) {
      drag.moved = true;
      setIsScrubbing(true);
      document.body.style.cursor = "ns-resize";
      document.body.style.userSelect = "none";
    }

    event.preventDefault();
    const safePixelsPerStep = Math.max(1, Number(pixelsPerStep) || 1);
    const stepCount = Math.trunc(distance / safePixelsPerStep);
    if (stepCount === drag.lastStepCount) return;

    drag.lastStepCount = stepCount;
    const safeStep = Number(step) || 1;
    onScrub(drag.startValue + stepCount * safeStep);
  };

  const onClick = (event) => {
    if (!suppressClickRef.current) return;
    suppressClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  return {
    isScrubbing,
    scrubHandlers: {
      onClick,
      onPointerCancel: finishScrub,
      onPointerDown,
      onPointerMove,
      onPointerUp: finishScrub,
    },
  };
};

export default useNumberScrub;
